import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Check, Inbox, Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { FilterMenu } from "@/components/ui/filter-menu";
import { EmptyState, LoadingState } from "@/components/shared/detail-view";
import { NotificationRow } from "@/components/notifications/NotificationRow";
import { useToast } from "@/hooks/use-toast";

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsFeed,
  useNudgeCategories,
} from "@/hooks/useNotificationsInbox";
import type {
  Nudge,
  NudgeCategory,
  NudgeStatusFilter,
} from "@/types/notifications";

const ALL = "all";

const Notifications = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [status, setStatus] = useState<NudgeStatusFilter>(ALL);
  const [category, setCategory] = useState<NudgeCategory>(ALL);

  // The taxonomy is the backend's, fetched at runtime — a category added there
  // must appear here without a Console release. If the registry call fails the
  // feed still works; the filter just narrows to "All".
  //
  // Nothing is filtered here. /nudges/console/categories/ returns console
  // categories only, and a rule this client applied would not be a rule — the
  // client can be modified, so the boundary belongs on the server.
  const { data: categories } = useNudgeCategories();

  const categoryMetaByKey = useMemo(
    () => new Map((categories ?? []).map((c) => [c.key, c])),
    [categories]
  );

  const categoryOptions = useMemo(
    () => [
      { value: ALL, label: "All categories" },
      ...(categories ?? []).map((c) => ({ value: c.key, label: c.label })),
    ],
    [categories]
  );

  const params = useMemo(
    () => ({
      status,
      ...(category === ALL ? {} : { category }),
    }),
    [status, category]
  );

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsFeed(params);

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data]
  );

  const clearableOnScreen = notifications.filter(
    (n) => n.read_at === null && n.can_mark_read
  ).length;

  const openNotification = (notification: Nudge) => {
    // A chat nudge clears by opening the conversation, not from the feed —
    // the endpoint returns 409 for one. Navigating is what clears it.
    if (notification.read_at === null && notification.can_mark_read) {
      markRead.mutate(notification.id);
    }
    if (notification.deep_link) {
      navigate(notification.deep_link);
    }
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate(category === ALL ? undefined : { category }, {
      onSuccess: (result) => {
        // Chat nudges are skipped rather than refused, so the badge can stay
        // lit after this. Say why, or it reads as a bug.
        if (result.skipped_count > 0) {
          toast({
            title:
              result.marked_count > 0
                ? `Marked ${result.marked_count} as read`
                : "Nothing to mark here",
            description: `${result.skipped_count} ${
              result.skipped_count === 1
                ? "conversation stays"
                : "conversations stay"
            } unread until you open ${
              result.skipped_count === 1 ? "it" : "them"
            }.`,
          });
        }
      },
    });
  };

  const categoryWord =
    category === ALL
      ? ""
      : `${categoryMetaByKey.get(category)?.label.toLowerCase() ?? ""} `;

  return (
    <div className="flex h-full overflow-hidden p-2 sm:p-3">
      <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="z-h1 text-foreground">
                Notifications
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Everything across the console that needs your attention.
              </CardDescription>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={clearableOnScreen === 0 || markAllRead.isPending}
            >
              {markAllRead.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Check />
              )}
              Mark all as read
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 min-h-0 flex flex-col gap-4 p-0">
          <div className="flex flex-col gap-3 px-6 sm:flex-row sm:items-center sm:justify-between">
            <Tabs
              value={status}
              onValueChange={(next) => setStatus(next as NudgeStatusFilter)}
            >
              <TabsList variant="line">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="unread">Unread</TabsTrigger>
              </TabsList>
            </Tabs>

            <FilterMenu
              label="Category"
              options={categoryOptions}
              value={category}
              onValueChange={(next) => setCategory(next)}
            />
          </div>

          <div className="flex-1 min-h-0 flex flex-col px-6 pb-6">
            {isLoading ? (
              <LoadingState label="Loading notifications..." />
            ) : isError ? (
              <EmptyState
                icon={AlertCircle}
                title="Couldn't load notifications"
                description={
                  (error as Error)?.message || "Please try again in a moment."
                }
              />
            ) : notifications.length === 0 ? (
              <EmptyState
                icon={status === "unread" ? Check : Inbox}
                title={
                  status === "unread"
                    ? "You're all caught up"
                    : `No ${categoryWord}notifications yet`
                }
                description={
                  status === "unread"
                    ? "Nothing unread here."
                    : "Anything that needs your attention will show up here."
                }
              />
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-sm">
                {notifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    categoryMeta={categoryMetaByKey.get(notification.category)}
                    onOpen={openNotification}
                  />
                ))}

                {hasNextPage ? (
                  <div className="flex justify-center p-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage ? (
                        <>
                          <Loader2 className="animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load older"
                      )}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
